import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import { IconButton } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import ConfirmationModal from "views/components/ConfirmationModal";
import { endpoint } from "assets/api/endpoint";
import { apiHandler } from "assets/api";
import CircularProgresss from "components/CircularProgress/CircularProgresss";
import ColumnGroupingTable from "components/UpdateUi/Table/Table";
import SubHeader from "components/UpdateUi/SubHeader/SubHeader";
import ToastModal from "views/components/ToastModal";
import { textFormat } from "utils/Utils";
import * as XLSX from "xlsx"; // ✅ Only this remains
import {
  negativeRegistryTableColumns,
  negativeRegistryFileColumns,
} from "assets/properties/properties";

const NegativeRegisteryMaster = () => {
  const navigate = useNavigate();
  const [rawData, setRawData] = useState([]);
  const [selectedTableData, setSelectedTableData] = useState([]);
  const [toast, setToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastMessageType, setToastMessageType] = useState("");
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [confirmationModalHeader, setConfirmationModalHeader] = useState("");
  const [confirmationModalMsg, setConfirmationModalMsg] = useState("");
  const [deleteId, setDeleteId] = useState("");
  const { authToken } = useSelector((state) => state.login.authData);
  const [callInProgress, setCallInProgress] = useState(false);

  const handleCreateDueDiligence = () => {
    navigate(`/coreservice/NegativeRegistery-create`);
  };

  const toastUpdate = (type, message, showToast) => {
    setToastMessageType(type);
    setToastMessage(message);
    setToast(showToast);
  };

  useEffect(() => {
    GetData();
  }, []);

  const GetData = async () => {
    setCallInProgress(true);
    const result = await apiHandler({
      url: endpoint.NEGATIVE_REGISTERY_FINDALL,
      authToken: authToken,
    });
    setCallInProgress(false);

    if (result?.data?.error_code) {
      toastUpdate("Error", result?.data?.user_description, true);
    } else {
      setRawData(result?.data);
      parseIndicatorData(result?.data);
    }
  };

  const parseIndicatorData = (data) => {
    let reportData = [];

    data &&
      data.forEach((role) => {
        reportData.push({
          name: textFormat(role.customerName),
          pan: role?.pan,
          uid: role?.adhaar,
          mobileNumber: role?.mobileNumber,
          risk: role?.risk ? "True" : "False",
          reason: role?.reason,
          action: getIcons(role.id),
        });
      });

    setSelectedTableData(reportData);
  };

  const getIcons = (id) => (
    <>
      <IconButton onClick={() => onClickUpdateData(id)}>
        <EditIcon />
      </IconButton>
      <IconButton onClick={() => onClickDeleteData(id)}>
        <DeleteIcon />
      </IconButton>
    </>
  );

  const onClickUpdateData = (id) => {
    navigate(`/coreservice/NegativeRegistery-create/` + id);
  };

  const onClickDeleteData = (id) => {
    setConfirmationModalHeader("Negative Registry");
    setConfirmationModalMsg("Are you sure you want to delete this entry");
    setDeleteId(id);
    setConfirmationModal(true);
  };

  const handleNegativeResponse = () => {
    setConfirmationModal(false);
    setDeleteId("");
  };

  const handlePositiveResponse = async () => {
    const roleid = deleteId;
    handleNegativeResponse();

    setCallInProgress(true);

    const res = await apiHandler({
      url: endpoint.NEGATIVE_REGISTERY_DELETE + roleid,
      method: "DELETE",
      authToken: authToken,
    });

    setCallInProgress(false);

    if (res?.data?.error_code) {
      toastUpdate("Error", res?.data?.user_description, true);
    } else {
      toastUpdate("Success", "Delete is successful", true);
      GetData();
    }
  };

  const onSearchChange = (event) => {
    const val = event.target.value;

    const filteredData = rawData.filter((row) =>
      row.customerName?.toLowerCase().includes(val.toLowerCase())
    );

    parseIndicatorData(filteredData);
  };

  const handleExportExcel = () => {
    const fileData = [];

    // Header
    fileData.push(negativeRegistryFileColumns);

    // Data
    rawData.forEach((row) => {
      fileData.push([
        textFormat(row.customerName),
        row.pan || "",
        row.adhaar || "",
        row.mobileNumber || "",
        row.risk ? "True" : "False",
        row.reason || "",
      ]);
    });

    exportNegativeRegistry(fileData, "NegativeRegistry.xlsx");
  };

  const exportNegativeRegistry = (fileData, fileName) => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(fileData);

    ws["!cols"] = [
      { wch: 25 },
      { wch: 20 },
      { wch: 20 },
      { wch: 15 },
      { wch: 15 },
      { wch: 30 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Negative Registry");

    XLSX.writeFile(wb, fileName);

    toastUpdate(
      "Success",
      "Negative registry data downloaded successfully",
      true
    );
  };

  return (
    <GridContainer>
      <GridItem xs={12}>
        <SubHeader
          label="Negative Registry"
          onSearchChange={onSearchChange}
          searchLabel="Search For Negative Registry"
          search
          button1="Create"
          button1click={handleCreateDueDiligence}
          button2={rawData?.length > 0 ? "Download" : ""}
          button2click={handleExportExcel}
        />
      </GridItem>

      <GridItem xs={12}>
        <ColumnGroupingTable
          columns={negativeRegistryTableColumns}
          rows={selectedTableData}
        />
      </GridItem>

      {toast && (
        <ToastModal
          toastMessage={toastMessage}
          toastMessageType={toastMessageType}
          toastUpdate={toastUpdate}
        />
      )}

      {callInProgress && (
        <CircularProgresss callInProgress={callInProgress} />
      )}

      <ConfirmationModal
        confirmationModal={confirmationModal}
        confirmationModalHeader={confirmationModalHeader}
        confirmationModalMsg={confirmationModalMsg}
        handleNegativeButton={handleNegativeResponse}
        handlePositiveButton={handlePositiveResponse}
      />
    </GridContainer>
  );
};

export default NegativeRegisteryMaster;